import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { Alert } from 'tea-component';
import DOMPurify from 'dompurify';
import { getCurrentDomain, TAdPlacementType } from './service';
import { useAdPlacement } from './useAdPlacement';
import './index.scss';

export interface MarqueeBannerProps {
  /** Current page full route string for backend base_url regex match. */
  baseUrl: string;
  /** Placement type, marquee by default; kept for future extension. */
  type?: TAdPlacementType;
  /** Add margin-top above the alert banner. */
  isMarginTop?: boolean;
  className?: string;
}

/**
 * Strategic MarqueeBanner (marquee): backend-driven ad placement by base_url + type.
 */
const MarqueeBanner: React.FC<MarqueeBannerProps> = ({
  baseUrl,
  type = 'marquee',
  isMarginTop = false,
  className,
}) => {
  const { marquees, loading, error } = useAdPlacement(baseUrl, type);
  const { locale } = useIntl();
  const marquee = marquees?.[0];

  const toAbsoluteUrl = (url?: string) => {
    if (!url) return '';
    return url.startsWith('http') ? url : `${getCurrentDomain()}${url}`;
  };

  // Replace any javascript:void(0) hrefs in HTML content with backend link.
  const processContentUrls = (content: string, link?: string): string => {
    if (!link) return content;
    const baseLink = toAbsoluteUrl(link);
    return content.replace(/href="javascript:void\(0\)"/g, `href="${baseLink}"`);
  };

  const getLocalizedText = () => {
    if (!marquee?.content) return '';
    const content = marquee.content;
    if (locale.startsWith('zh')) {
      return content.text_cn || content.text || content.text_en || '';
    }
    return content.text_en || content.text || content.text_cn || '';
  };

  const getContent = () => {
    if (loading || error || !marquee?.content) return null;
    const raw = getLocalizedText();
    if (!raw) return null;

    const processed = processContentUrls(raw, marquee?.content?.link);
    // Sanitize HTML before injecting to prevent XSS.
    const sanitized = DOMPurify.sanitize(processed);

    if (marquee?.content?.link && !/href\s*=/.test(processed)) {
      return (
        <a
          href={toAbsoluteUrl(marquee.content.link)}
          target="_blank"
          rel="noreferrer"
        >
          <span dangerouslySetInnerHTML={{ __html: sanitized }} />
        </a>
      );
    }

    return <div dangerouslySetInnerHTML={{ __html: sanitized }} />;
  };

  const content = getContent();
  const closeable = marquee?.content?.closeable !== false;
  const adStyle: React.CSSProperties = {
    // Keep the default gradient background from SCSS for now.
    // We only apply backend text color and closeable behavior.
    ...(marquee?.content?.text_color
      ? {
        ['--dbs-alert-text-color' as any]: marquee.content.text_color,
        color: marquee.content.text_color,
      }
      : {}),
  };

  // Show only when backend has content and it is not still loading / errored.
  if (!content) return null;

  return (
    <Alert
      defaultVisible
      style={adStyle}
      className={classnames('dbs-alert-tip', className, {
        'dbs-alert-tip--margin-top': isMarginTop,
        'dbs-alert-tip--non-closeable': !closeable,
      })}
    >
      {content}
    </Alert>
  );
};

export default MarqueeBanner;
